import React, { useMemo, useState } from "react";
import { History, Search, CircleDot } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, Input, Select, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { loadAccessibleVehicles, loadAllocationHistory } from "@/lib/staffData";
import { formatReg, fmtDate, fmtDateTime } from "@/lib/vda";

function Cell({ l, v }) {
  return (
    <div>
      <div className="vda-tick text-muted-foreground">{l}</div>
      <div className="mt-0.5 text-sm">{v ?? "—"}</div>
    </div>
  );
}

export default function AllocationHistory() {
  const { data: vehicles, loading: vLoading } = useAsync(() => loadAccessibleVehicles(), []);
  const [vehicleId, setVehicleId] = useState("");
  const [q, setQ] = useState("");

  const selectedId = vehicleId || vehicles?.[0]?.id || "";
  const { data: history, loading: hLoading } = useAsync(
    () => (selectedId ? loadAllocationHistory(selectedId) : Promise.resolve([])),
    [selectedId]
  );

  const filteredVehicles = useMemo(() => {
    const list = vehicles || [];
    if (!q) return list;
    return list.filter((v) => `${v.registration} ${v.make} ${v.model} ${v.customers?.name || ""}`.toLowerCase().includes(q.toLowerCase()));
  }, [vehicles, q]);

  if (vLoading) return <StaffShell><Spinner /></StaffShell>;
  const selected = (vehicles || []).find((v) => v.id === selectedId);

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Read-only</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1 flex items-center gap-2">
        <History className="h-5 w-5" /> Allocation history
      </h1>
      <p className="text-sm text-muted-foreground mb-5">Derived from the driver_allocations record. Historical allocations are preserved and cannot be edited here.</p>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        {/* vehicle picker */}
        <div>
          <Panel className="p-3">
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="alloc-vehicle-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search registration" className="pl-9" />
            </div>
            <div className="max-h-[60vh] overflow-auto divide-y divide-border">
              {filteredVehicles.map((v) => (
                <button key={v.id} data-testid={`alloc-vehicle-${v.registration.replace(/\s/g, "")}`}
                  onClick={() => setVehicleId(v.id)}
                  className={`w-full text-left px-2 py-2.5 hover:bg-secondary ${v.id === selectedId ? "bg-secondary" : ""}`}>
                  <div className="vda-reg font-medium">{formatReg(v.registration)}</div>
                  <div className="text-xs text-muted-foreground">{v.make} {v.model} · {v.customers?.name}</div>
                </button>
              ))}
              {filteredVehicles.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No vehicles</p>}
            </div>
          </Panel>
        </div>

        {/* timeline */}
        <div>
          {selected && (
            <div className="mb-4">
              <div className="vda-reg text-xl font-semibold text-primary">{formatReg(selected.registration)}</div>
              <div className="text-sm text-muted-foreground">{selected.make} {selected.model} · {selected.customers?.name}</div>
            </div>
          )}
          {hLoading ? <Spinner /> : (history || []).length === 0 ? (
            <EmptyState title="No allocation records" hint="This vehicle has no allocation history." />
          ) : (
            <div className="space-y-3" data-testid="alloc-history-list">
              {history.map((a) => (
                <Panel key={a.id} className={`p-4 ${a.status === "active" ? "border-l-2 border-l-primary" : ""}`} data-testid={`alloc-row-${a.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CircleDot className={`h-4 w-4 ${a.status === "active" ? "text-primary" : "text-muted-foreground/50"}`} />
                      <span className="font-medium">{a.driver_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag tone={a.status === "active" ? "green" : "muted"}>{a.status === "active" ? "Active" : "Historical"}</Tag>
                      {a.is_primary && <Tag tone="clay">Primary</Tag>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                    <Cell l="Allocation start" v={fmtDate(a.start_at)} />
                    <Cell l="Allocation end" v={a.end_at ? fmtDate(a.end_at) : "Ongoing"} />
                    <Cell l="Source" v={a.source} />
                    <Cell l="Import batch" v={a.import_file} />
                    <Cell l="Record created" v={fmtDateTime(a.created_at)} />
                    <Cell l="Applied / approved by" v={a.approved_by || (a.source === "seed" ? "Seed data" : null)} />
                  </div>
                  {a.ended_by && <p className="mt-3 text-xs text-muted-foreground">Ended by {a.ended_by}</p>}
                </Panel>
              ))}
            </div>
          )}
        </div>
      </div>
    </StaffShell>
  );
}
